import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import {
  FeeType,
  FeeItemProperty,
  IOrderChargesFeeItems,
  IOrderPayment,
  IOrdersReceipt,
  OrderPaymentType,
  OrderSpecialFeeType,
} from 'src/types/OrderHistory';
import { GRUBHUB_PLUS_LABEL } from 'src/constants/brands';

export function isGrubhubPlusPayment(payment: IOrderPayment): boolean {
  const subscriptionCode = get(payment, 'metadata', 'subscription_code');
  const subscriptionCobrand =
    get(payment, 'metadata', 'subscription_cobrand') ?? '';

  return (
    payment.type === OrderPaymentType.PROMO_CODE &&
    !!subscriptionCode &&
    subscriptionCobrand.startsWith(GRUBHUB_PLUS_LABEL)
  );
}

export function getCampaignDeliveryFeeDisplayText(
  order: IOrdersReceipt
): string | undefined {
  return order?.savings_breakdown?.fee_savings?.[FeeType.DELIVERY]
    ?.display_text;
}

const combinedServiceFeeTypes = [
  OrderSpecialFeeType.SERVICE,
  OrderSpecialFeeType.CHAIN_SERVICE_FEE,
  OrderSpecialFeeType.SERVICE_TOLL,
];

export function getFeeItemProperty(
  order: IOrdersReceipt,
  feeType: string,
  property: FeeItemProperty
): number {
  const feeItems = get(order, 'order_charges', 'order_fees', 'fee_items') || [];
  const feeItem = feeItems.find((item) => item.type === feeType);
  return feeItem?.[property] ?? 0;
}

export function calculateDeliveryFee(order: IOrdersReceipt): number {
  const campaignDeliveryFee = getCampaignDeliveryFeeDisplayText(order);

  const property = campaignDeliveryFee
    ? FeeItemProperty.NON_ADJUSTED_AMOUNT
    : FeeItemProperty.CALCULATED_AMOUNT;

  return getFeeItemProperty(order, OrderSpecialFeeType.DELIVERY, property);
}

export function calculateServiceFee(order: IOrdersReceipt): number {
  const campaignServiceFee =
    order?.savings_breakdown?.fee_savings?.[FeeType.SERVICE]?.display_text;

  const property = campaignServiceFee
    ? FeeItemProperty.NON_ADJUSTED_AMOUNT
    : FeeItemProperty.CALCULATED_AMOUNT;

  return combinedServiceFeeTypes
    .map((type) => getFeeItemProperty(order, type, property))
    .reduce((total, currentAmount) => total + currentAmount, 0);
}

export function getGenericFees(order: IOrdersReceipt): IOrderChargesFeeItems[] {
  const feeItems = get(order, 'order_charges', 'order_fees', 'fee_items') || [];
  return feeItems.filter((feeItem) => {
    const isSpecialFeeType = Object.values(OrderSpecialFeeType).includes(
      feeItem.type as OrderSpecialFeeType
    );
    return !isSpecialFeeType;
  });
}
